// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { Color, Line, LinearGradientBrush, Pen, PolyLine, PolyRect, SolidBrush } from '@shared/commons/Graphics';
import { ProfilePOCTypesEnum, ProfileVATypesEnum } from '../Series/TerceraChartCashItemSeriesSettings';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Periods } from '@shared/utils/History/TFInfo';
import { DynProperty, PairColor } from '@shared/commons/DynProperty';
import { TerceraChartDrawingType, TerceraChartRendererDrawingAdvancedParamsEnum, TerceraChartToolRendererDrawingType } from '../Utils/ChartConstants';
import { LayersEnum } from '../Utils/LayersEnum';
/// Рендерер для исторических данных из кешитема
export class TerceraChartMainPriceRenderer extends TerceraChartBaseRenderer {
    // this.SpreadAskBidSessionFlag;   // #100139
    // this.SpreadLastSessionFlag;
    constructor(terceraChart) {
        super(terceraChart);
        this.Series = null;
        this.marketProfileTextFont = ThemeManager.Fonts.Font_12F_regular;
        this.ChartDrawingType = TerceraChartDrawingType.Candle;
        this.UseInAutoscale = true;
        // TEST.
        this.TEST_CHART_TYPES = [
            TerceraChartDrawingType.Candle,
            TerceraChartDrawingType.Bar,
            TerceraChartDrawingType.LinesBreak,
            TerceraChartDrawingType.Renko,
            TerceraChartDrawingType.Line,
            TerceraChartDrawingType.Dot,
            TerceraChartDrawingType.DotLine,
            TerceraChartDrawingType.Solid,
            TerceraChartDrawingType.Forest,
            TerceraChartDrawingType.Kagi,
            TerceraChartDrawingType.TicTac,
            TerceraChartDrawingType.Profile,
            TerceraChartDrawingType.Cluster
        ];
        this.TEST_CURRENT_CHART_TYPE_IDX = 0;
        this.SetClassName('TerceraChartMainPriceRenderer');
    }
    get SolidPriceBrushColor() {
        return this.solidPriceColorBrush.ColorStart;
    }
    set SolidPriceBrushColor(value) {
        this.solidPriceColorBrush = new LinearGradientBrush(0, 0, 1, 1, Color.FromArgb(127, value), Color.FromArgb(0, value));
    }
    ThemeChanged() {
        this.barsHiLowColorPen = new Pen(ThemeManager.colors.Chart_BarsHiLowColor);
        this.histogramLineColorPen = new Pen(ThemeManager.colors.Chart_HistogramLineColor);
        this.barsLineColorPen = new Pen(ThemeManager.colors.Chart_SolidPriceColor, 2);
        this.solidPriceColorBrush = new LinearGradientBrush(0, 0, 1, 1, ThemeManager.colors.Chart_SolidPriceColor_Gradient_Start, ThemeManager.colors.Chart_SolidPriceColor_Gradient_End);
        this.clusterRegularBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterRegularFontColor);
        this.clusterMaxTextBrush = new SolidBrush('white');
        this.clusterBorderPen = new Pen(ThemeManager.colors.Chart_ClusterBorderColor);
        this.clusterColorSchemeDeltaSellSourceBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterColorSchemeDeltaSellSourceColor);
        this.clusterColorSchemeDeltaBuySourceBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterColorSchemeDeltaBuySourceColor);
        this.clusterMaxBackBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterMaxBackColor);
        this.clusterMaxTextBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterMaxTextColor);
        this.clusterTradesSchemeSourceBrush = this.clusterVolumeSchemeSourceBrush = new SolidBrush(ThemeManager.colors.Chart_ClusterVolumeSchemeColor);
        this.marketProfileRegularBrush = new SolidBrush(ThemeManager.colors.Chart_MarketProfileRegularColor);
        this.marketProfileVAVerticalPen = this.marketProfileVAHorisontalPen = new Pen(ThemeManager.colors.Chart_MarketProfileVAColor, 3);
        this.marketProfilePocPen = new Pen(ThemeManager.colors.Chart_MarketProfilePocColor);
        this.marketProfilePocTriangleBrush = new SolidBrush(ThemeManager.colors.Chart_MarketProfilePocColor);
        this.marketProfileSinglesPen = new Pen(ThemeManager.colors.Chart_MarketProfileSingleColor);
        this.marketProfilePositiveBrush = new SolidBrush(ThemeManager.colors.Chart_MarketProfilePositiveColor);
        this.marketProfileNegativeBrush = new SolidBrush(ThemeManager.colors.Chart_MarketProfileNegativeColor);
        this.marketProfileVolumeColor = ThemeManager.colors.Chart_MarketProfileVolumeColor;
        this.marketProfileGradientColor = ThemeManager.colors.Chart_MarketProfileGradientColor;
        this.wickUpBorderColorPen = new Pen(ThemeManager.colors.Chart_WickUpBorderColor);
        this.wickDownBorderColorPen = new Pen(ThemeManager.colors.Chart_WickDownBorderColor);
        this.barsUpColorBrush = new SolidBrush(ThemeManager.colors.Chart_BarsUpColor);
        this.barsDownColorBrush = new SolidBrush(ThemeManager.colors.Chart_BarsDownColor);
        this.barsUpBorderColorPen = new Pen(ThemeManager.colors.Chart_BarsUpBorderColor);
        this.barsDownBorderColorPen = new Pen(ThemeManager.colors.Chart_BarsDownBorderColor);
    }
    IsNeedDraw(numberOfLayer) {
        return this.assignLayer === numberOfLayer || LayersEnum.Quotes === numberOfLayer;
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        if (!this.Visible ||
            this.Series == null ||
            this.InvalidState /* || !(this.Series is TerceraChartCashItemSeries) */) {
            return;
        }
        advParams.exitAfterFirst = advParams.layerId === LayersEnum.Quotes;
        //
        // После рисования - режим Foreground
        // TerceraChartRendererDrawingAdvancedParams param = (TerceraChartRendererDrawingAdvancedParams)advParams;
        advParams.Tag[TerceraChartRendererDrawingAdvancedParamsEnum.CurrentDrawingType] =
            TerceraChartToolRendererDrawingType.Foreground;
        // bool Additional = this is TerceraChartOverlayRenderer;
        /// /
        /// / Режим редактирования
        /// /
        // if (pro.EditMode && pro.ActiveMainPriceInd == this)
        // {
        //    editMode = true;
        // }
        //
        // общий набор для всех рисовалок
        //
        const clientRect = window.ClientRectangle;
        gr.save();
        gr.beginPath();
        gr.rect(clientRect.X, clientRect.Y, clientRect.Width, clientRect.Height);
        gr.clip();
        const cashItemSeries = this.Series; // s as TerceraChartCashItemSeries;
        const scX = window.XScale;
        let curX = clientRect.X + clientRect.Width - scX;
        const screenData = cashItemSeries.ChartScreenData.Storage;
        if (screenData.length > 0) {
            const lastScreenDate = screenData[screenData.length - 1].Time;
            curX = Math.ceil(window.PointsConverter.GetScreenXbyTime(lastScreenDate));
        }
        let leftBorder = 0;
        let barW = 1;
        const procData = TerceraChartMainPriceRenderer.ProcessBarWidth(window);
        leftBorder = procData.leftBorder;
        barW = procData.barW;
        // this.SpreadAskBidSessionFlag = advParams.Instrument.Level1.getBidAskSessionFlag();
        // this.SpreadLastSessionFlag = cashItemSeries.SyncronizedSessionFlag ? cashItemSeries.SyncronizedSessionFlag : advParams.Instrument.Level1.getLastSessionFlag();
        switch (this.ChartDrawingType) {
            //
            // Candles
            //
            case TerceraChartDrawingType.Candle:
            case TerceraChartDrawingType.Bar:
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                this.DrawCandleChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries, advParams);
                break;
            case TerceraChartDrawingType.LinesBreak:
            case TerceraChartDrawingType.Renko:
                this.DrawCandleChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
                break;
            //
            // Рисование линий
            //
            case TerceraChartDrawingType.Line:
            case TerceraChartDrawingType.Dot:
            case TerceraChartDrawingType.DotLine:
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                //
                this.DrawLineChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries, advParams);
                break;
            //
            // Рисование Solid
            //
            case TerceraChartDrawingType.Solid:
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                //
                this.DrawLineChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries, advParams);
                break;
            case TerceraChartDrawingType.Forest:
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                //
                this.DrawForestChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
                break;
            case TerceraChartDrawingType.Kagi:
                this.DrawKagiChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                break;
            case TerceraChartDrawingType.TicTac:
                // Draw zero line
                TerceraChartMainPriceRenderer.DrawZeroLineForRelative(gr, window, clientRect, cashItemSeries);
                this.DrawTicTacChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
                break;
            //   case TerceraChartDrawingType.Cluster:
            //     this.DrawClusterChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
            //     break;
            case TerceraChartDrawingType.Profile:
                this.DrawProfileChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries);
                break;
        }
        // Reset clip.
        gr.restore();
    }
    DrawCandleChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries, advParams) {
        const screenData = cashItemSeries.ChartScreenData.Storage;
        const clientRect = window.ClientRectangle;
        let i;
        const len = screenData.length;
        let firstObject = true;
        const exitAfterFirst = advParams.exitAfterFirst;
        const wickPolyLines = new PolyLine();
        const fallenPolyLines = new PolyLine();
        const greenPolyLines = new PolyLine();
        const fallenPolyRects = new PolyRect();
        const greenPolyRects = new PolyRect();
        for (i = len - 1; i >= 0; i--) {
            const scrDataItem = screenData[i];
            const closePrice = scrDataItem.Close;
            const openPrice = scrDataItem.Open;
            const highPrice = scrDataItem.High;
            const lowPrice = scrDataItem.Low;
            if (screenData[i].Hole || screenData[i].NotShowExtendedSession) {
                curX -= scX;
                continue;
            }
            let closeY = window.PointsConverter.GetScreenY(closePrice);
            let openY = window.PointsConverter.GetScreenY(openPrice);
            let highY = window.PointsConverter.GetScreenY(highPrice);
            let lowY = window.PointsConverter.GetScreenY(lowPrice);
            const res = TerceraChartMainPriceRenderer.FitBarByClientRectangle(clientRect, closeY, openY, highY, lowY);
            if (!res.boolValue) {
                curX -= scX;
                continue;
            }
            if (firstObject && !exitAfterFirst) {
                curX -= scX;
                firstObject = false;
                continue;
            }
            closeY = res.closeY;
            openY = res.openY;
            highY = res.highY;
            lowY = res.lowY;
            const fallenBar = openPrice > closePrice;
            const wickBar = closePrice - openPrice === 0;
            const addBarH = closeY - openY === 0 ? 1 : 0;
            let barMIddle = Math.floor(curX + (leftBorder + barW) / 2);
            const barRectX = barMIddle - Math.floor(barW / 2);
            if (barW === 1) {
                barMIddle = curX;
            }
            if (wickBar) {
                wickPolyLines.lines.push(new Line(barMIddle, highY, barMIddle, lowY));
                let leftBorderX = barRectX;
                let richtBorderX = barRectX + barW;
                if ((richtBorderX - leftBorderX) % 2 !== 0) {
                    richtBorderX--;
                }
                if (leftBorderX === richtBorderX) {
                    richtBorderX++;
                }
                if (barW === 1) {
                    leftBorderX = richtBorderX;
                }
                wickPolyLines.lines.push(new Line(leftBorderX, openY, richtBorderX, openY));
            }
            else if (fallenBar) {
                switch (this.ChartDrawingType) {
                    case TerceraChartDrawingType.Bar:
                        fallenPolyLines.lines.push(new Line(barMIddle, highY, barMIddle, lowY));
                        fallenPolyLines.lines.push(new Line(barRectX, openY, barMIddle, openY));
                        let wid = curX + leftBorder + barW;
                        if (barW === 1) {
                            wid = barMIddle;
                        }
                        fallenPolyLines.lines.push(new Line(barMIddle, closeY, wid, closeY));
                        break;
                    default:
                        fallenPolyLines.lines.push(new Line(barMIddle, highY, barMIddle, openY));
                        fallenPolyLines.lines.push(new Line(barMIddle, lowY, barMIddle, closeY));
                        if (barW === 1) {
                            fallenPolyLines.lines.push(new Line(curX + leftBorder, openY, curX + leftBorder, closeY));
                        }
                        else {
                            const h = closeY - openY + addBarH;
                            fallenPolyRects.rects.push(new Rectangle(barRectX, openY, barW, h));
                        }
                        break;
                }
            }
            else {
                switch (this.ChartDrawingType) {
                    case TerceraChartDrawingType.Bar:
                        greenPolyLines.lines.push(new Line(barMIddle, highY, barMIddle, lowY));
                        greenPolyLines.lines.push(new Line(barRectX, openY, barMIddle, openY));
                        let wid = curX + leftBorder + barW;
                        if (barW === 1) {
                            wid = barMIddle;
                        }
                        greenPolyLines.lines.push(new Line(barMIddle, closeY, wid, closeY));
                        break;
                    default:
                        greenPolyLines.lines.push(new Line(barMIddle, highY, barMIddle, openY));
                        greenPolyLines.lines.push(new Line(barMIddle, lowY, barMIddle, closeY));
                        if (barW === 1) {
                            greenPolyLines.lines.push(new Line(curX + leftBorder, openY, curX + leftBorder, closeY));
                        }
                        else {
                            const h = closeY - openY + addBarH;
                            greenPolyRects.rects.push(new Rectangle(barRectX, openY, barW, h));
                        }
                        break;
                }
            }
            curX -= scX;
            if (exitAfterFirst) {
                break;
            }
        }
        gr.DrawPolyLine(this.barsHiLowColorPen, wickPolyLines);
        gr.DrawPolyLine(this.ChartDrawingType === TerceraChartDrawingType.Bar
            ? this.barsDownBorderColorPen
            : this.wickDownBorderColorPen, fallenPolyLines);
        gr.DrawPolyLine(this.ChartDrawingType === TerceraChartDrawingType.Bar
            ? this.barsUpBorderColorPen
            : this.wickUpBorderColorPen, greenPolyLines);
        gr.DrawPolyRect(this.barsDownColorBrush, fallenPolyRects, this.barsDownBorderColorPen);
        gr.DrawPolyRect(this.barsUpColorBrush, greenPolyRects, this.barsUpBorderColorPen);
    }
    DrawLineChart(gr, window, // Replace 'any' with the actual type for 'window'.
    scX, // Replace 'number' with the appropriate data type.
    curX, // Replace 'number' with the appropriate data type.
    leftBorder, // Replace 'number' with the appropriate data type.
    barW, // Replace 'number' with the appropriate data type.
    cashItemSeries, // Replace 'any' with the actual type for 'cashItemSeries'.
    advParams // Replace 'any' with the actual type for 'advParams'.
    ) {
        const screenData = cashItemSeries.ChartScreenData.Storage;
        const period = cashItemSeries.CashItem.FPeriod;
        let points = [];
        const points2 = [];
        let rects = [];
        const barDirection = [];
        const dotW = barW > 1 ? 2 : 1;
        const dotDelta = barW > 1 ? 1 : 0;
        const pConverter = window.PointsConverter;
        const firstObject = true;
        const isTic = this.Series._cashItem.FPeriod === Periods.TIC;
        const exitAfterFirst = advParams.exitAfterFirst;
        if (!exitAfterFirst) {
            return;
        }
        // if (isTic && exitAfterFirst)
        //     return;
        //
        // Iterate through all bars
        //
        const len = screenData.length;
        for (let i = len - 1; i >= 0; i--) {
            const scrDataItem = screenData[i];
            if (scrDataItem.Hole) {
                this.DrawPartLines(gr, window, points, points2, period, rects, barDirection);
                points = [];
                rects = [];
            }
            const closePrice = scrDataItem.Close;
            const openPrice = scrDataItem.Open;
            //
            // Only one line is not drawn for ticks.
            // BUG #29345  (HDM) Only Bid or only Ask tick line does not drawing
            // 1. When one of them is missing, draw the other.
            if (!openPrice && !closePrice) {
                curX -= scX;
                continue;
            }
            // if (!isTic && firstObject && !exitAfterFirst)
            // {
            //     curX -= scX;
            //     firstObject = false;
            //     continue;
            // }
            const closeY = pConverter.GetScreenY(closePrice);
            const openY = pConverter.GetScreenY(openPrice);
            // Explicitly incorrect values will cause an exception during drawing
            if ((closePrice && (closeY < -1000 || closeY > 10000)) || (openPrice && (openY < -1000 || openY > 10000))) {
                curX -= scX;
                continue;
            }
            const barMIddle = barW === 1 ? curX : curX + (leftBorder + barW) / 2;
            //
            // Only one line is not drawn for ticks.
            // BUG #29345  (HDM) Only Bid or only Ask tick line does not drawing
            // 2. Don't filter out the one that is missing
            if (closePrice) {
                points.push(new Point(barMIddle, closeY));
            }
            if (openPrice && period === Periods.TIC) {
                points2.push(new Point(barMIddle, openY));
            }
            const ChartDrawingType = this.ChartDrawingType;
            if (ChartDrawingType === TerceraChartDrawingType.Dot ||
                ChartDrawingType === TerceraChartDrawingType.DotLine) {
                if (closePrice) {
                    rects.push(new Rectangle(barMIddle - dotDelta, closeY, dotW, dotW));
                    barDirection.push(openPrice > closePrice);
                }
            }
            else if (ChartDrawingType === TerceraChartDrawingType.Solid) {
                if (points2.length === 0) {
                    points2.push(new Point(barMIddle, closeY));
                }
                else if (closeY < points2[0].Y) {
                    points2[0] = new Point(barMIddle, closeY);
                }
            }
            curX -= scX;
            // if (exitAfterFirst && points.length > 1)
            //     break;
        }
        this.DrawPartLines(gr, window, points, points2, period, rects, barDirection);
    }
    DrawPartLines(gr, window, // Replace 'any' with the actual type for 'window'.
    points, // Define the appropriate type for 'points'.
    points2, // Define the appropriate type for 'points2'.
    period, // Define the appropriate type for 'period'.
    rects, // Define the appropriate type for 'rects'.
    barDirection // Define the appropriate type for 'barDirection'.
    ) {
        // var mode = gr.SmoothingMode;
        // gr.SmoothingMode = SmoothingMode.HighQuality;
        const p_len = points.length;
        const p2_len = points2.length;
        //
        // Drawing lines
        //
        if (period !== Periods.TIC) {
            if (p_len > 1) {
                const rects_len = rects.length;
                switch (this.ChartDrawingType) {
                    case TerceraChartDrawingType.Line:
                        gr.DrawCurve(this.barsLineColorPen, points);
                        break;
                    case TerceraChartDrawingType.Dot:
                        for (let i = 0; i < rects_len; i++) {
                            const rect = rects[i];
                            gr.FillEllipse(barDirection[i] ? this.barsDownColorBrush : this.barsUpColorBrush, rect.X - 2.5, rect.Y - 3, rect.Width + 5, rect.Height + 5);
                        }
                        break;
                    case TerceraChartDrawingType.DotLine:
                        for (let i = 0; i < rects_len; i++) {
                            if (i > 0) {
                                const pen = barDirection[i - 1]
                                    ? this.barsDownBorderColorPen
                                    : this.barsUpBorderColorPen;
                                const prevPoint = points[i - 1];
                                const currPoint = points[i];
                                gr.DrawLine(pen, prevPoint.X - 1, prevPoint.Y, currPoint.X - 1, currPoint.Y);
                            }
                            const rect = rects[i];
                            gr.DrawEllipse(barDirection[i] ? this.barsDownBorderColorPen : this.barsUpBorderColorPen, rect.X - 2.5, rect.Y - 3, rect.Width + 5, rect.Height + 5);
                            gr.FillEllipse(barDirection[i] ? this.barsDownColorBrush : this.barsUpColorBrush, rect.X - 2.5, rect.Y - 3, rect.Width + 5, rect.Height + 5);
                        }
                        break;
                    case TerceraChartDrawingType.Solid:
                        const CR = window.ClientRectangle;
                        const bottom = CR.Y + CR.Height;
                        // to close polygon
                        points.push(new Point(points[p_len - 1].X, bottom));
                        points.push(new Point(points[0].X, bottom));
                        let solidPriceColorBrush = this.solidPriceColorBrush;
                        const firstP2Point = points2[0];
                        solidPriceColorBrush = new LinearGradientBrush(firstP2Point.X, firstP2Point.Y, firstP2Point.X, bottom, solidPriceColorBrush.ColorStart, solidPriceColorBrush.ColorEnd);
                        gr.DrawFilledCurve(solidPriceColorBrush, points);
                        // remove temp points
                        points.splice(p_len, 2);
                        gr.DrawCurve(this.barsLineColorPen, points);
                        break;
                }
            }
        }
        //
        // Drawing ticks
        //
        else {
            if (p_len > 1)
                gr.DrawLines(this.wickDownBorderColorPen, points);
            if (p2_len > 1)
                gr.DrawLines(this.wickUpBorderColorPen, points2);
        }
        // gr.SmoothingMode = mode;
    }
    DrawForestChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries) {
        const screenData = cashItemSeries.ChartScreenData.Storage;
        // Find the last close price
        let lastClose = 0;
        let lclose = screenData.length - 1;
        while (lclose > 0 && lastClose === 0) {
            lastClose = screenData[lclose].Close;
            lclose--;
        }
        // Draw it on the chart
        const curIntY = window.PointsConverter.GetScreenY(lastClose);
        if (!(curIntY < -1000 || curIntY > 10000)) {
            const clientRect = window.ClientRectangle;
            const clientRectX = clientRect.X;
            gr.DrawLine(this.histogramLineColorPen, clientRectX, curIntY, clientRectX + clientRect.Width, curIntY);
        }
        //
        // Iterate through all bars
        //
        for (let i = screenData.length - 1; i >= 0; i--) {
            const closePrice = screenData[i].Close;
            if (!closePrice) {
                curX -= scX;
                continue;
            }
            const closeY = window.PointsConverter.GetScreenY(closePrice);
            // Explicitly incorrect values will cause an exception during drawing
            if (closePrice && (closeY < -1000 || closeY > 10000)) {
                curX -= scX;
                continue;
            }
            const barMIddle = Math.floor(curX + (leftBorder + barW) / 2);
            gr.DrawLine(this.histogramLineColorPen, barMIddle, curIntY, barMIddle, closeY);
            curX -= scX;
        }
    }
    DrawKagiChart(gr, // Replace 'any' with the actual type for 'gr'.
    window, // Replace 'any' with the actual type for 'window'.
    scX, // Replace 'number' with the appropriate data type.
    curX, // Replace 'number' with the appropriate data type.
    leftBorder, // Replace 'number' with the appropriate data type.
    barW, // Replace 'number' with the appropriate data type.
    cashItemSeries // Replace 'any' with the actual type for 'cashItemSeries'.
    ) {
        const screenData = cashItemSeries.ChartScreenData.Storage;
        const scrData_len = screenData.length;
        const boldLines = {};
        const barsUpBorderColorPen = this.barsUpBorderColorPen;
        const restoreWidth = barsUpBorderColorPen.Width;
        try {
            if (scrData_len > 0) {
                const scrDataFirst = screenData[0];
                boldLines[0] = scrDataFirst.Close > scrDataFirst.Open;
            }
            for (let i = 1; i < scrData_len; i++) {
                const prevScrDataItem = screenData[i - 1];
                const close = screenData[i].Close;
                if (close > prevScrDataItem.High) {
                    boldLines[i] = true;
                }
                else if (close < prevScrDataItem.Low) {
                    boldLines[i] = false;
                }
                else {
                    boldLines[i] = boldLines[i - 1];
                }
            }
            barsUpBorderColorPen.Width *= 4;
            const barsDownPen = this.barsDownBorderColorPen;
            const pConverter = window.PointsConverter;
            let list = [];
            let isBoldState = boldLines[scrData_len - 1];
            for (let i = scrData_len - 1; i >= 0; i--) {
                const scrDataItem = screenData[i];
                const dv1 = scrDataItem.Close;
                if (dv1) {
                    const dv0 = scrDataItem.Open;
                    const dv2 = scrDataItem.High;
                    const dv3 = scrDataItem.Low;
                    const cv0 = pConverter.GetScreenY(dv0);
                    const cv1 = pConverter.GetScreenY(dv1);
                    const cv2 = pConverter.GetScreenY(dv2);
                    const cv3 = pConverter.GetScreenY(dv3);
                    const curBoldState = boldLines[i];
                    if (isBoldState !== curBoldState) {
                        // If barstyle is used, the list might be empty
                        // Change the bold state anyway; otherwise, the last line will be drawn poorly
                        if (list.length > 0) {
                            const p1 = list[list.length - 1];
                            list.splice(list.length - 1);
                            const changePoint = new Point(p1.X, cv0);
                            list.push(changePoint);
                            gr.DrawLines(isBoldState ? barsUpBorderColorPen : barsDownPen, list);
                            list = [changePoint, p1];
                        }
                        isBoldState = curBoldState;
                    }
                    const x = curX + scX;
                    if (x < 0) {
                        if (list.length === 0) {
                            continue;
                        }
                        const prev = list[list.length - 1];
                        list.push(new Point(1, prev.Y));
                    }
                    else
                        list.push(new Point(x, cv1)); // c
                    if (dv0 - dv1 > 0.0) {
                        // Falling market
                        list.push(new Point(curX, cv3)); // l
                        list.push(new Point(curX, cv2)); // h
                    }
                    else {
                        // Rising market
                        list.push(new Point(curX, cv2)); // h
                        list.push(new Point(curX, cv3)); // l
                    }
                }
                curX -= scX;
            }
            if (list.length > 0) {
                gr.DrawLines(isBoldState ? barsUpBorderColorPen : barsDownPen, list);
            }
        }
        finally {
            barsUpBorderColorPen.Width = restoreWidth;
        }
    }
    DrawTicTacChart(gr, // Replace 'any' with the actual type for 'gr'.
    window, // Replace 'any' with the actual type for 'window'.
    scX, // Replace 'number' with the appropriate data type.
    curX, // Replace 'number' with the appropriate data type.
    leftBorder, // Replace 'number' with the appropriate data type.
    barW, // Replace 'number' with the appropriate data type.
    cashItemSeries // Replace 'any' with the actual type for 'cashItemSeries'.
    ) {
        const tt = cashItemSeries.CashItem;
        if (!tt)
            return;
        const ttHeight = tt.Height;
        const pConverter = window.PointsConverter;
        const screenData = cashItemSeries.ChartScreenData.Storage;
        const scrData_len = screenData.length;
        for (let i = scrData_len - 1; i >= 0; i--) {
            const scrDataItem = screenData[i];
            const dv1 = scrDataItem.Close;
            if (dv1) {
                const dv0 = scrDataItem.Open;
                const dv2 = scrDataItem.High;
                const dv3 = scrDataItem.Low;
                const curCx = curX;
                const count = Math.round((dv2 - dv3) / ttHeight);
                for (let j = 0; j < count; j++) {
                    const y_h = dv2 - ttHeight * j;
                    const y_l = dv2 - ttHeight * (j + 1);
                    const cv2 = pConverter.GetScreenY(y_h); // h
                    const cv3 = pConverter.GetScreenY(y_l); // l
                    if (dv0 > dv1) {
                        // +++denis #13818
                        const barsDownBorderColorPen = this.barsDownBorderColorPen;
                        if (barsDownBorderColorPen) {
                            const ticTacH = Math.abs(cv2 - cv3);
                            if (ticTacH < 2) {
                                gr.DrawLine(barsDownBorderColorPen, curCx, cv2, curCx + barW, cv2);
                            }
                            else {
                                gr.DrawEllipse(barsDownBorderColorPen, curCx, cv2, barW, ticTacH);
                            }
                        }
                    }
                    else {
                        const barsUpBorderColorPen = this.barsUpBorderColorPen;
                        if (barsUpBorderColorPen) {
                            const ticTacH = Math.abs(cv2 - cv3);
                            if (ticTacH < 2) {
                                gr.DrawLine(barsUpBorderColorPen, curCx, cv2, curCx + barW, cv2);
                            }
                            else {
                                gr.DrawLine(barsUpBorderColorPen, curCx, cv2, curCx + barW, cv3);
                                gr.DrawLine(barsUpBorderColorPen, curCx, cv3, curCx + barW, cv2);
                            }
                        }
                    }
                }
            }
            curX -= scX;
        }
    }
    // TODO. Profile data is required.
    DrawProfileChart(gr, window, scX, curX, leftBorder, barW, cashItemSeries) {
        const pConverter = window.PointsConverter;
        const height = this.marketProfileTextFont.Height;
        this.cashSettings.profileMinimumStep = pConverter.GetDataY(0) - pConverter.GetDataY(height);
        const screenData = cashItemSeries.ChartScreenData.Storage;
        const scrData_len = screenData.length;
        const settings = cashItemSeries.settings;
        for (let i = scrData_len - 1; i >= 0; i--) {
            const data = screenData[i];
            /* if (data.Hole || data.profileData == null) {
                      curX -= scX;
                      continue;
                  } */
            const profileData = data.profileData;
            const x = curX;
            const isColorsByElement = settings.ProfileIsColorsByElement;
            // POC
            if (settings.profilePOCType === ProfilePOCTypesEnum.Line) {
                const pocLevel = pConverter.GetScreenY(profileData.maxPrice);
                gr.DrawLine(this.marketProfilePocPen, x, pocLevel, x + scX - 15, pocLevel);
                const pocPoints = [
                    new Point(x + scX - 10, pocLevel),
                    new Point(x + scX - 5, pocLevel - 4),
                    new Point(x + scX - 5, pocLevel + 3)
                ];
                gr.FillPolygon(this.marketProfilePocTriangleBrush, pocPoints);
            }
            // VA
            if (settings.profileVAType !== ProfileVATypesEnum.None) {
                const y1 = pConverter.GetScreenY(profileData.va1) - height / 2;
                const y2 = pConverter.GetScreenY(profileData.va2) + height / 2;
                switch (settings.profileVAType) {
                    case ProfileVATypesEnum.Vertical:
                        gr.DrawLine(this.marketProfileVAVerticalPen, x, y2, x, y1);
                        break;
                    case ProfileVATypesEnum.Horizontal:
                        gr.DrawLine(this.marketProfileVAVerticalPen, x, y2, x, y1);
                        gr.DrawLine(this.marketProfileVAHorisontalPen, x, y1, x + scX - 5, y1);
                        gr.DrawLine(this.marketProfileVAHorisontalPen, x, y2, x + scX - 5, y2);
                        break;
                    case ProfileVATypesEnum.ToNextProfile:
                        gr.DrawLine(this.marketProfileVAVerticalPen, x, y2, x, y1);
                        gr.DrawLine(this.marketProfileVAHorisontalPen, x, y1, x + 2 * scX - 5, y1);
                        gr.DrawLine(this.marketProfileVAHorisontalPen, x, y2, x + 2 * scX - 5, y2);
                        break;
                }
                if (settings.profileVAType === ProfileVATypesEnum.Horizontal ||
                    settings.profileVAType === ProfileVATypesEnum.ToNextProfile) {
                    const gradientBrush = new LinearGradientBrush(x, y1, x + scX, y1, this.marketProfileGradientColor, 'transparent');
                    gr.FillRect(gradientBrush, x, y1, scX, y2 - y1);
                }
            }
            // Singles
            if (profileData.singles) {
                const singles = profileData.singles;
                const singles_len = singles.length;
                for (let singles_i = 0; singles_i < singles_len; singles_i++) {
                    const y = pConverter.GetScreenY(singles[singles_i]);
                    gr.DrawLine(this.marketProfileSinglesPen, x, y, x + scX - 5, y);
                }
            }
            // Profile
            const profile = profileData.profile;
            const profile_keys = Object.keys(profile);
            const profile_len = profile_keys.length;
            for (let profile_i = 0; profile_i < profile_len; profile_i++) {
                const profile_key = profile_keys[profile_i];
                const textValue = profile[profile_key];
                const price = profile_key;
                const level = pConverter.GetScreenY(price);
                if (isColorsByElement) {
                    const marketProfileTextFont = this.marketProfileTextFont;
                    let textElementPosition = curX;
                    const textValueSplits = textValue('');
                    const txtVal_len = textValueSplits.length;
                    for (let txtVal_i = 0; txtVal_i < txtVal_len; txtVal_i++) {
                        const textElement = textValueSplits[txtVal_i];
                        const brush = this.ProfileGetBrush(settings, profileData, parseFloat(price), txtVal_i);
                        gr.DrawString(textElement, marketProfileTextFont, brush, textElementPosition, level - height / 2);
                        textElementPosition += gr.measureText(textElement).width;
                    }
                }
                else {
                    const brush = this.ProfileGetBrush(settings, profileData, parseFloat(price), 0);
                    gr.DrawString(textValue, this.marketProfileTextFont, brush, curX, level - height / 2);
                }
            }
            curX -= scX;
        }
    }
    ProfileGetBrush(settings, data, price, index) {
        if (!settings || !data) {
            return this.marketProfileRegularBrush;
        }
        if (!data.colors) {
            return this.marketProfileRegularBrush;
        }
        const values = [];
        const mode = settings.profileVolumeType;
        return '';
        // switch (mode) {
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.Single:
        //         // "бесцветная" схема
        //         return this.marketProfileRegularBrush;
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.DeltaProfile:
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.UpdownProfile:
        //         // Red/Green бары
        //         const deltaProfile = data.colors;
        //         return deltaProfile <= 0 ? this.marketProfileNegativeBrush : this.marketProfilePositiveBrush;
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.DeltaPrice:
        //         // Red/Green уровни цен
        //         const deltaPrices = data.colors;
        //         if (deltaPrices.hasOwnProperty(price)) {
        //             return deltaPrices[price] <= 0 ? this.marketProfileNegativeBrush : this.marketProfilePositiveBrush;
        //         } else {
        //             return this.marketProfileRegularBrush;
        //         }
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.VolumePrice:
        //         // уровни с прозрачностью
        //         const volumePrices = data.colors; // There's a variable name missing here; replace with the correct one
        //         if (volumePrices.hasOwnProperty(price)) {
        //             return new SolidBrush(Color.FromArgb(volumePrices[price], this.marketProfileVolumeColor);
        //         } else {
        //             return this.marketProfileRegularBrush;
        //         }
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.DeltaBars:
        //         // Red/Green поэлементно
        //         let deltaBars = data.colors;
        //         values = deltaBars[price] || [];
        //         if (!deltaBars.hasOwnProperty(price) || index >= values.length) {
        //             return this.marketProfileRegularBrush;
        //         }
        //         const deltaBarVal = values[index];
        //         return new SolidBrush(Color.FromArgb(
        //             Math.abs(deltaBarVal),
        //             deltaBarVal <= 0 ? this.marketProfileNegativeBrush.Color : this.marketProfilePositiveBrush.Color
        //         ));
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.UpdownBars:
        //         // Red/Green поэлементно
        //         let deltaBars = data.colors; // Same variable name as above; replace with the correct one
        //         values = deltaBars[price] || [];
        //         if (!deltaBars.hasOwnProperty(price)) {
        //             return this.marketProfileRegularBrush;
        //         }
        //         if (index >= values.length) {
        //             return this.marketProfileRegularBrush;
        //         }
        //         return values[index] <= 0 ? this.marketProfileNegativeBrush : this.marketProfilePositiveBrush;
        //     case TerceraChartCashItemSeriesSettings.ProfileColoringTypes.VolumeBars:
        //         // поэлементно с прозрачностью
        //         const volumeBars = data.colors; // There's a variable name missing here; replace with the correct one
        //         values = volumeBars[price] || [];
        //         if (!volumeBars.hasOwnProperty(price)) {
        //             return this.marketProfileRegularBrush;
        //         }
        //         if (index >= values.length) {
        //             return this.marketProfileRegularBrush;
        //         }
        //         return new SolidBrush(Color.FromArgb(
        //             values[index],
        //             this.marketProfileVolumeColor
        //         ));
        //     default:
        //         return this.marketProfileRegularBrush;
        // }
    }
    // #endregion
    // #region Cluster Chart
    // TODO. Cluster data, VolumeInfo are required.
    //   DrawClusterChart (
    //     gr: any, // Replace 'any' with the actual type for 'gr'.
    //     window: any, // Replace 'any' with the actual type for 'window'.
    //     scX: number, // Replace 'number' with the appropriate data type.
    //     curX: number, // Replace 'number' with the appropriate data type.
    //     leftBorder: number, // Replace 'number' with the appropriate data type.
    //     barW: number, // Replace 'number' with the appropriate data type.
    //     cashItemSeries: any // Replace 'any' with the actual type for 'cashItemSeries'.
    //   ) {
    //     const screenData = cashItemSeries.ChartScreenData.Storage;
    //     const scrData_len = screenData.length;
    //     const pConverter = window.PointsConverter;
    //     const clientRect = window.ClientRectangle;
    //     const MULTIPLIER_SIGN = TerceraChartMainPriceRenderer.MULTIPLIER_SIGN;
    //     const clusterTextFont = this.clusterTextFont;
    //     clusterTextFont.applySettings(gr);
    //     const mulSignWidth = gr.measureText(MULTIPLIER_SIGN).width;
    //     const textHeight = clusterTextFont.Height;
    //     const textHeightHalf = textHeight / 2;
    //     const cashSettings = this.cashSettings;
    //     cashSettings.clusterMinimumStep = pConverter.GetDataY(0) - pConverter.GetDataY(textHeight);
    //     const ci = cashItemSeries.CashItem;
    //     const tickData = ci && ci.FPeriod === Periods.TIC;
    //     const clusterBorderPen = this.clusterBorderPen;
    //     const clusterDownPen = this.clusterDownPen;
    //     const clusterUpPen = this.clusterUpPen;
    //     for (let i = scrData_len - 1; i >= 0; i--) {
    //       const data = screenData[i];
    //       /* if (data.Hole) {
    //                 curX -= scX;
    //                 continue;
    //             } */
    //       const closePrice = VolumeInfo.GetPrice(data.Close, cashSettings.clusterCurrentStep);
    //       const openPrice = VolumeInfo.GetPrice(data.Open, cashSettings.clusterCurrentStep);
    //       const highPrice = VolumeInfo.GetPrice(tickData ? closePrice : data.High, cashSettings.clusterCurrentStep);
    //       const lowPrice = VolumeInfo.GetPrice(tickData ? closePrice : data.Low, cashSettings.clusterCurrentStep);
    //       const closeY = pConverter.GetScreenY(closePrice);
    //       const openY = pConverter.GetScreenY(openPrice);
    //       const highY = pConverter.GetScreenY(highPrice);
    //       const lowY = pConverter.GetScreenY(lowPrice);
    //       const res = TerceraChartMainPriceRenderer.FitBarByClientRectangle(clientRect, closeY, openY, highY, lowY);
    //       if (!res.boolValue) {
    //         curX -= scX;
    //         continue;
    //       }
    //       const boxHeight = pConverter.GetScreenY(closePrice) - pConverter.GetScreenY(closePrice + cashSettings.clusterCurrentStep);
    //       const boxHeightHalf = Math.round(boxHeight / 2) + 1;
    //       const fallenBar = openPrice > closePrice;
    //       const grownBar = openPrice < closePrice;
    //       const clusters = data.clusterData;
    //       if (clusters && clusters.length > 0) {
    //         const left = curX + leftBorder;
    //         const prices = this.DrawBarClusterData(gr, window, barW, clusters, textHeightHalf, boxHeight, mulSignWidth, left);
    //         // #region корректировка цен
    //         // корректирую рамку бара, чтобы гарантировано вместились все трейды
    //         if (prices.length > 0) {
    //           const max = prices[0];
    //           const min = prices[prices.length - 1];
    //           if (max > highPrice) { highY = pConverter.GetScreenY(max); }
    //           if (min < lowPrice) { lowY = pConverter.GetScreenY(min); }
    //         }
    //         // корректирую OHLC, чтобы не пересекали цены уровня
    //         highY -= boxHeightHalf - 1;
    //         lowY += boxHeightHalf;
    //         if (fallenBar) {
    //           openY -= boxHeightHalf;
    //           closeY += boxHeightHalf;
    //         } else if (grownBar) {
    //           openY += boxHeightHalf;
    //           closeY -= boxHeightHalf;
    //         }
    //         // #endregion
    //         gr.DrawRect(clusterBorderPen, left, highY, barW, lowY - highY);
    //         if (!tickData) {
    //           if (fallenBar) { gr.DrawLine(clusterDownPen, left, openY, left, closeY); } else if (grownBar) { gr.DrawLine(clusterUpPen, left, openY, left, closeY); }
    //         }
    //       }
    //       curX -= scX;
    //     }
    //   }
    //   DrawBarClusterData (
    //     gr: any, // Replace 'any' with the actual type for 'gr'.
    //     window: any, // Replace 'any' with the actual type for 'window'.
    //     barW: number, // Replace 'number' with the appropriate data type.
    //     clusters: any[], // Replace 'any[]' with the actual type for 'clusters'.
    //     textHeightHalf: number, // Replace 'number' with the appropriate data type.
    //     boxHeight: number, // Replace 'number' with the appropriate data type.
    //     mulSignWidth: number, // Replace 'number' with the appropriate data type.
    //     left: number // Replace 'number' with the appropriate data type.
    //   ) {
    //     const pConverter = window.PointsConverter;
    //     // Prices are the same for every "clusters" item.
    //     const prices = Object.keys(clusters[0].StrValues);
    //     prices.sort(TerceraChartMainPriceRenderer.DrawBarClusterDataPriceComparator);
    //     const prices_len = prices.length;
    //     const CLUSTER_TEXT_MARGIN_PX = TerceraChartMainPriceRenderer.CLUSTER_TEXT_MARGIN_PX;
    //     const MULTIPLIER_SIGN = TerceraChartMainPriceRenderer.MULTIPLIER_SIGN;
    //     for (let i = 0; i < prices_len; i++) {
    //       const price = prices[i];
    //       const scrPriceY = pConverter.GetScreenY(price);
    //       const textYTop = scrPriceY - textHeightHalf;
    //       const boxYTop = scrPriceY - boxHeight / 2;
    //       const valuesWidthList: number[] = [];
    //       const res = this.GetClusterTextWidth(gr, clusters, price, mulSignWidth, valuesWidthList);
    //       let textWidth = res.textWidth;
    //       let valueWidth = res.clusterTextWidth;
    //       const drawElipsis = barW - 2 < textWidth;
    //       if (drawElipsis) {
    //         const res = this.GetClusterTextWidth(gr, clusters, price, mulSignWidth, valuesWidthList, true);
    //         textWidth = res.textWidth;
    //         valueWidth = res.clusterTextWidth;
    //       }
    //       const textPadding = (barW - textWidth) / 2;
    //       let startTextX = left + textPadding + (drawElipsis ? 0 : (valueWidth - valuesWidthList[0]));
    //       const clusters_len = clusters.length;
    //       for (let c_i = 0; c_i < clusters_len; c_i++) {
    //         const cluster = clusters[c_i];
    //         // Value background.
    //         const valueBackBrush = this.GetValueBackBrush(cluster, price, clusterColorScheme);
    //         if (valueBackBrush) {
    //           if (clusters_len === 1) {
    //             // Fill the entire bar width.
    //             gr.FillRect(valueBackBrush, left, boxYTop, barW, boxHeight);
    //           } else {
    //             if (c_i === 0) { gr.FillRect(valueBackBrush, left, boxYTop, valueWidth + (startTextX - left), boxHeight); } else if (c_i === clusters_len - 1) { gr.FillRect(valueBackBrush, startTextX, boxYTop, valueWidth + barW - (startTextX + valueWidth - left), boxHeight); } else { gr.FillRect(valueBackBrush, startTextX, boxYTop, valueWidth, boxHeight); }
    //           }
    //         }
    //         // Value text.
    //         gr.DrawString(drawElipsis ? '...' : cluster.StrValues[price],
    //           clusterTextFont, this.GetValueBrush(cluster, price), startTextX, textYTop);
    //         startTextX += drawElipsis ? valueWidth : valuesWidthList[c_i];
    //         // Space between values.
    //         if (c_i + 1 < clusters_len) {
    //           startTextX += CLUSTER_TEXT_MARGIN_PX;
    //           gr.DrawString(MULTIPLIER_SIGN, clusterTextFont, clusterRegularBrush, startTextX, textYTop);
    //           startTextX += mulSignWidth;
    //           startTextX += CLUSTER_TEXT_MARGIN_PX;
    //         }
    //       }
    //     }
    //     return prices;
    //   }
    //   GetClusterTextWidth (
    //     gr: any, // Replace 'any' with the actual type for 'gr'.
    //     clusters: any[], // Replace 'any[]' with the actual type for 'clusters'.
    //     price: number, // Replace 'number' with the appropriate data type.
    //     mulSignWidth: number, // Replace 'number' with the appropriate data type.
    //     valuesWidthList: number[],
    //     elipsis?: boolean
    //   ) {
    //     let textWidth = 0;
    //     let clusterTextWidth = 0;
    //     if (elipsis) {
    //       clusterTextWidth = gr.measureText('...').width;
    //     } else {
    //       const len = clusters.length;
    //       for (let i = 0; i < len; i++) {
    //         const cluster = clusters[i];
    //         const formattedValue = cluster.StrValues[price];
    //         const valueWidth = gr.measureText(formattedValue).width;
    //         valuesWidthList.push(valueWidth);
    //         if (clusterTextWidth < valueWidth) { clusterTextWidth = valueWidth; }
    //       }
    //     }
    //     textWidth =
    //             clusterTextWidth * len +
    //             (TerceraChartMainPriceRenderer.CLUSTER_TEXT_MARGIN_PX + mulSignWidth) *
    //             (len - 1);
    //     return {
    //       textWidth,
    //       clusterTextWidth
    //     };
    //   }
    //   GetValueBrush (cluster: any, price: number) {
    //     return this.clusterHighlightMax && cluster.MaxList.hasOwnProperty(price)
    //       ? this.clusterMaxTextBrush
    //       : this.clusterRegularBrush;
    //   }
    //   GetValueBackBrush (cluster: any, price: number, colorScheme: any) {
    //     if (this.clusterHighlightMax && cluster.MaxList.hasOwnProperty(price)) {
    //       return this.clusterMaxBackBrush;
    //     } else {
    //       let sourceColor = '';
    //       let min = 0; let max = 0;
    //       let curValue = 0;
    //       switch (colorScheme) {
    //         case ClusterColorScheme.ByDelta: {
    //           const clusterDelta = cluster.ClusterDelta;
    //           if (clusterDelta) {
    //             curValue = clusterDelta.Values[price];
    //             const buySide = curValue >= 0;
    //             min = 0;
    //             max = buySide ? clusterDelta.MaxValue : Math.abs(clusterDelta.MinValue);
    //             sourceColor = buySide
    //               ? this.clusterColorSchemeDeltaBuySourceBrush.Color
    //               : this.clusterColorSchemeDeltaSellSourceBrush.Color;
    //             if (!buySide) curValue = Math.abs(curValue);
    //           }
    //           break;
    //         }
    //         case ClusterColorScheme.ByVolume: {
    //           const clusterVolume = cluster.ClusterVolume;
    //           if (clusterVolume) {
    //             curValue = clusterVolume.Values[price];
    //             min = clusterVolume.MinValue;
    //             max = clusterVolume.MaxValue;
    //             sourceColor = this.clusterVolumeSchemeSourceBrush.Color;
    //           }
    //           break;
    //         }
    //         case ClusterColorScheme.ByTrades: {
    //           const clusterTrades = cluster.ClusterTrades;
    //           if (clusterTrades) {
    //             curValue = clusterTrades.Values[price];
    //             min = clusterTrades.MinValue;
    //             max = clusterTrades.MaxValue;
    //             sourceColor = this.clusterTradesSchemeSourceBrush.Color;
    //           }
    //           break;
    //         }
    //       }
    //       if (!sourceColor) { return null; }
    //       this.colorSchemeBrush.Color = Color.GetLighterColor(
    //         min, max, curValue, sourceColor, 0.2);
    //       return this.colorSchemeBrush;
    //     }
    //   }
    static DrawBarClusterDataPriceComparator(p1, p2) {
        return p2 - p1;
    }
    static DrawZeroLineForRelative(gr, // Replace 'any' with the actual type for 'gr'.
    window, // Replace 'any' with the actual type for 'window'.
    clientRect, // Replace 'any' with the actual type for 'clientRect'.
    cashItemSeries // Replace 'any' with the actual type for 'cashItemSeries'.
    ) {
        // #52890 - пока не удаляю, если будут реквесты от пользователей, нужно будет добавить возможности кастомизации zero line
        // if (cashItemSeries.settings.DataType === TerceraChartCashItemSeriesDataType.Relative) {
        //     const zeroY = window.PointsConverter.GetScreenY(0);
        //     const clientRectX = clientRect.X;
        //     gr.DrawLine(new Pen('red'), clientRectX, zeroY, clientRectX + clientRect.Width, zeroY);
        // }
    }
    static ProcessBarWidth(window) {
        const barWidth = window.XScale;
        let barW = 1;
        let leftBorder = 0;
        if (barWidth <= 3) {
            barW = 1;
            leftBorder = barWidth - barW;
        }
        else if (barWidth <= 6) {
            barW = 3;
            leftBorder = barWidth - barW;
        }
        else if (barWidth <= 8) {
            barW = 5;
            leftBorder = barWidth - barW;
        }
        else if (barWidth <= 10) {
            barW = 7;
            leftBorder = barWidth - barW;
        }
        else if (barWidth <= 12) {
            barW = 9;
            leftBorder = barWidth - barW;
        }
        else if (barWidth <= 14) {
            barW = 11;
            leftBorder = barWidth - barW;
        }
        else {
            const coefPaddingW = 0.8;
            barW = Math.floor(barWidth * coefPaddingW);
            if (barW % 2 === 0) {
                barW -= 1;
            }
            leftBorder = barWidth - barW;
        }
        return { barW, leftBorder };
    }
    static FitBarByClientRectangle(clientRect, // Replace 'any' with the actual type for 'clientRect'.
    closeY, // Replace 'number' with the appropriate data type.
    openY, // Replace 'number' with the appropriate data type.
    highY, // Replace 'number' with the appropriate data type.
    lowY // Replace 'number' with the appropriate data type.
    ) {
        const top = clientRect.Y;
        const bottom = clientRect.Y + clientRect.Height;
        if (highY < top || lowY > bottom) {
            if (lowY < top || highY > bottom) {
                return { boolValue: false };
            }
            else {
                if (closeY < top && openY < top) {
                    // Handle this case if needed.
                }
                else {
                    if (closeY < top)
                        closeY = top;
                    if (openY < top)
                        openY = top;
                }
                if (highY < top)
                    highY = top;
                if (closeY > bottom)
                    closeY = bottom;
                if (openY > bottom)
                    openY = bottom;
                if (lowY > bottom)
                    lowY = bottom;
            }
        }
        return { boolValue: true, highY, closeY, openY, lowY };
    }
    Properties() {
        const properties = super.Properties();
        let prop;
        // if (this.ChartDrawingType === this.TerceraChartDrawingType.Cluster && this.cashSettings !== null) {
        //     //#region Cluster properties
        //     // ... (Translated cluster properties here)
        //     //#endregion
        // } else if (this.ChartDrawingType === TerceraChartDrawingType.Profile && this.cashSettings !== null) {
        //     //#region Market Profile properties
        //     // ... (Translated Market Profile properties here)
        //     //#endregion
        // } else {
        const dp = new DynProperty('MainPriceVisibility', this.Visible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        dp.sortIndex = 0;
        properties.push(dp);
        // Add colors
        const bodyColor = new PairColor(this.barsUpColorBrush.Color, this.barsDownColorBrush.Color, Resources.getResource('propery.MainPriceRenderer.Up'), Resources.getResource('propery.MainPriceRenderer.Down'));
        prop = new DynProperty('bodyColor', bodyColor, DynProperty.PAIR_COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [
            TerceraChartDrawingType.Candle,
            TerceraChartDrawingType.Dot,
            TerceraChartDrawingType.DotLine
        ];
        prop.sortIndex = 2;
        properties.push(prop);
        const borderColor = new PairColor(this.barsUpBorderColorPen.Color, this.barsDownBorderColorPen.Color, Resources.getResource('propery.MainPriceRenderer.Up'), Resources.getResource('propery.MainPriceRenderer.Down'));
        prop = new DynProperty('borderColor', borderColor, DynProperty.PAIR_COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Bar, TerceraChartDrawingType.Candle];
        prop.sortIndex = 3;
        properties.push(prop);
        const wickColor = new PairColor(this.wickUpBorderColorPen.Color, this.wickDownBorderColorPen.Color, Resources.getResource('propery.MainPriceRenderer.Up'), Resources.getResource('propery.MainPriceRenderer.Down'));
        prop = new DynProperty('wickColor', wickColor, DynProperty.PAIR_COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Candle];
        prop.sortIndex = 4;
        properties.push(prop);
        prop = new DynProperty('barsHiLowColor', this.barsLineColorPen.Color, DynProperty.COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Line, TerceraChartDrawingType.Solid];
        prop.sortIndex = 7;
        properties.push(prop);
        prop = new DynProperty('solidPriceColor', this.solidPriceColorBrush.ColorStart, DynProperty.COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Solid];
        prop.sortIndex = 8;
        properties.push(prop);
        prop = new DynProperty('HistogramLineColor', this.histogramLineColorPen.Color, DynProperty.COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Forest];
        prop.sortIndex = 6;
        properties.push(prop);
        prop = new DynProperty('Doji', this.barsHiLowColorPen.Color, DynProperty.COLOR, DynProperty.DATA_STYLE_GROUP);
        prop.enabilityValue = [TerceraChartDrawingType.Bar, TerceraChartDrawingType.Candle];
        prop.sortIndex = 5;
        properties.push(prop);
        // }
        return properties;
    }
    callBack(properties) {
        // Cluster chart properties
        // ...
        // Market Profile
        // ...
        let prop = DynProperty.getPropertyByName(properties, 'MainPriceVisibility');
        if (prop)
            this.Visible = prop.value;
        // Apply colors
        prop = DynProperty.getPropertyByName(properties, 'bodyColor');
        if (prop !== null) {
            this.barsUpColorBrush = new SolidBrush(prop.value.Color1);
            this.barsDownColorBrush = new SolidBrush(prop.value.Color2);
        }
        prop = DynProperty.getPropertyByName(properties, 'borderColor');
        if (prop !== null) {
            this.barsUpBorderColorPen = new Pen(prop.value.Color1);
            this.barsDownBorderColorPen = new Pen(prop.value.Color2);
        }
        prop = DynProperty.getPropertyByName(properties, 'wickColor');
        if (prop !== null) {
            this.wickUpBorderColorPen = new Pen(prop.value.Color1);
            this.wickDownBorderColorPen = new Pen(prop.value.Color2);
        }
        prop = DynProperty.getPropertyByName(properties, 'barsHiLowColor');
        if (prop !== null) {
            this.barsLineColorPen.Color = prop.value;
        }
        prop = DynProperty.getPropertyByName(properties, 'solidPriceColor');
        if (prop !== null) {
            this.solidPriceColorBrush.ColorStart = prop.value;
        }
        prop = DynProperty.getPropertyByName(properties, 'HistogramLineColor');
        if (prop !== null) {
            this.histogramLineColorPen.Color = prop.value;
        }
        prop = DynProperty.getPropertyByName(properties, 'Doji');
        if (prop !== null) {
            this.barsHiLowColorPen.Color = prop.value;
        }
    }
    /**
     * Renderer participates in automatic scaling calculation.
     */
    FindMinMax(refObj, window) {
        refObj.tMin = Number.MAX_VALUE;
        refObj.tMax = -Number.MAX_VALUE;
        if (this.Series === null) {
            return false;
        }
        // Use cached data
        refObj.tMin = this.Series.ChartScreenData.MinLow;
        refObj.tMax = this.Series.ChartScreenData.MaxHigh;
        return true;
    }
}
TerceraChartMainPriceRenderer.MULTIPLIER_SIGN = 'x';
TerceraChartMainPriceRenderer.CLUSTER_TEXT_MARGIN_PX = 2;
